import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { DateRangePicker } from 'react-dates';
import {
  Badge,
  Button,
  Collapse,
  FormGroup,
  Input,
  Label,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import {
  addSelectedFilter,
  deleteSelectedFilter,
} from '../../actions/Files';

import FiltersResetButton from './FiltersResetButton';

import { slugify } from '../../helpers';
import {
  getDateRanges,
  getExpirationDateRanges,
  getDateRange,
} from '../../helpers/DateTime';
import {
  getMinFileCreatedAt,
  getMinFileOriginalDatetime,
  getMinFileExpirationDatetime,
} from '../../helpers/Files';
import { handleComponentParams } from '../../helpers/URLParams';

// eslint-disable-next-line react/prop-types
const renderMonthElement = ({ month, onMonthSelect, onYearSelect }, dateMode) => {
  let min = getMinFileCreatedAt();
  if (dateMode === 'original') {
    min = getMinFileOriginalDatetime();
  }
  if (dateMode === 'expiration') {
    min = getMinFileExpirationDatetime();
  }

  const returnYears = () => {
    const max = moment().format('YYYY-MM-DD');
    const years = [];
    for (
      let i = parseInt(moment(min).format('YYYY'));
      i <= parseInt(moment(max).format('YYYY'));
      i += 1
    ) {
      years.push(<option key={i} value={i}>{i}</option>);
    }
    return years;
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div>
        <Input
          type="select"
          bsSize="sm"
          value={
            moment(month).isBefore(moment(min)) ?
              moment(min).month() :
              month.month()
          }
          onChange={(e) => {
            if (month.isBefore(moment(min))) {
              onMonthSelect(moment(min), moment(min).month());
            } else {
              onMonthSelect(month, e.target.value);
            }
          }}
        >
          {moment.months().map((label, value) => (
            <option key={slugify(label)} value={value}>{label}</option>
          ))}
        </Input>
      </div>
      <div>
        <Input
          type="select"
          bsSize="sm"
          value={month.year()}
          onChange={(e) => {
            onYearSelect(month, e.target.value);
          }}
        >
          {returnYears()}
        </Input>
      </div>
    </div>
  );
};

class FiltersFileDates extends Component {
  constructor(props) {
    super(props);

    this.state = {
      focusedInput: null,
      startDate: moment().subtract(7, 'days'),
      endDate: moment(),
    };

    this.handleChangeFilter = this.handleChangeFilter.bind(this);
    this.handleChangeDates = this.handleChangeDates.bind(this);
    this.handleReset = this.handleReset.bind(this);
  }

  componentDidMount() {
    const { dateMode } = this.props;

    let dateParams = ['date_from', 'date_to'];
    if (dateMode === 'original') dateParams = ['original_date_from', 'original_date_to'];
    if (dateMode === 'expiration') dateParams = ['expiration_date_from', 'expiration_date_to'];

    handleComponentParams(
      true,
      dateParams,
      null,
      [
        (filter) => this.handleDateFilter(filter, 'from'),
        (filter) => this.handleDateFilter(filter, 'to'),
      ],
      () => {},
    );
  }

  handleDateFilter(filter, type) {
    const { startDate, endDate } = this.state;
    if (!filter) return;
    if (type === 'from') {
      const dateTimeFilter = moment(`${filter} 00:00:00`);
      if (!startDate.isSame(dateTimeFilter)) {
        this.setState({ startDate: dateTimeFilter });
      }
    } else if (type === 'to') {
      const dateTimeFilter = moment(`${filter} 23:59:59`);
      if (!endDate.isSame(dateTimeFilter)) {
        this.setState({ endDate: dateTimeFilter });
      }
    }
  }

  getDates(range) {
    if (range.id === 'custom') {
      const {
        startDate,
        endDate,
      } = this.state;
      return {
        from: startDate.format('YYYY-MM-DD 00:00:00'),
        to: endDate.format('YYYY-MM-DD 23:59:59'),
      };
    }

    if (range.id === 'expired') {
      return {
        from: null,
        to: moment().utc().subtract().format('YYYY-MM-DD HH:mm:ss'),
      };
    }

    if (this.getFilterType() === 'expiration_dates') {
      return {
        to: moment().utc().subtract(range.days, 'days').format('YYYY-MM-DD HH:mm:ss'),
        from: moment().utc().subtract().format('YYYY-MM-DD HH:mm:ss'),
      };
    }

    return {
      from: moment().utc().subtract(range.days, 'days').format('YYYY-MM-DD HH:mm:ss'),
      to: moment().utc().subtract().format('YYYY-MM-DD HH:mm:ss'),
    };
  }

  getFilterTitle() {
    const { intl, dateMode } = this.props;

    switch (dateMode) {
      case 'original':
        return intl.formatMessage({ defaultMessage: 'Creation date' });
      case 'expiration':
        return intl.formatMessage({ defaultMessage: 'Expiration date' });
      default:
        return intl.formatMessage({ defaultMessage: 'Upload date' });
    }
  }

  getFilterType() {
    const { dateMode } = this.props;

    switch (dateMode) {
      case 'original':
        return 'original_dates';
      case 'expiration':
        return 'expiration_dates';
      default:
        return 'dates';
    }
  }

  getFilterField() {
    const { dateMode } = this.props;

    switch (dateMode) {
      case 'original':
        return 'original_date';
      case 'expiration':
        return 'expiration_date';
      default:
        return 'date';
    }
  }

  getFilterFieldName() {
    const { dateMode } = this.props;

    switch (dateMode) {
      case 'original':
        return 'originalDate';
      case 'expiration':
        return 'expirationDate';
      default:
        return 'uploadDate';
    }
  }

  handleChangeFilter(e) {
    const { value } = e.target;
    const {
      intl,
      handleChangeFilters,
      addFilter,
      removeFilter,
    } = this.props;

    const range = getDateRange(value, intl);
    if (range) {
      const dates = this.getDates(range);
      let { label } = range;
      if (value === 'custom') {
        label = `${moment(dates.from).format('MM/DD/YYYY')} - ${moment(dates.to).format('MM/DD/YYYY')}`;
      }

      removeFilter(this.getFilterType());
      addFilter({
        type: this.getFilterType(),
        key: `${value}`,
        label,
        title: this.getFilterTitle(),
      });

      if (value === 'custom') {
        handleChangeFilters({
          [this.getFilterField()]: value,
          [`${this.getFilterField()}_from`]: dates.from,
          [`${this.getFilterField()}_to`]: dates.to,
        });
      } else {
        handleChangeFilters({
          [this.getFilterField()]: value,
          [`${this.getFilterField()}_from`]: null,
          [`${this.getFilterField()}_to`]: null,
        });
      }
    }
  }

  handleChangeDates(dates) {
    this.setState({
      startDate: dates.startDate,
      endDate: dates.endDate,
    }, () => {
      if (dates.startDate && dates.endDate) {
        this.handleChangeFilter({ target: { value: 'custom' } });
      }
    });
  }

  handleReset() {
    const {
      handleChangeFilters,
      clearSelectedFilters,
    } = this.props;

    handleChangeFilters({
      [this.getFilterField()]: null,
      [`${this.getFilterField()}_from`]: null,
      [`${this.getFilterField()}_to`]: null,
    });
    clearSelectedFilters(this.getFilterType());
  }

  render() {
    const {
      focusedInput,
      startDate,
      endDate,
    } = this.state;
    const {
      intl,
      filters,
      dateMode,
      isOpen,
      handleToggleFilter,
      prefix,
    } = this.props;
    const filter = filters[this.getFilterField()];
    const dateRanges = dateMode === 'expiration' ? getExpirationDateRanges(intl) : getDateRanges(intl);

    return (
      <div className="files-filter upload-date-filter">
        <div className="filter-header">
          <Button onClick={handleToggleFilter} className="filter-title">
            <i className={`fal fa-angle-${isOpen ? 'up' : 'down'}`} />
            <span>{this.getFilterTitle()}</span>
            {' '}
            {filter && (
              <Badge color="primary">
                {intl.formatMessage({ defaultMessage: '1' })}
              </Badge>
            )}
          </Button>
          {filter && (
            <FiltersResetButton
              handleReset={this.handleReset}
            />
          )}
        </div>
        <Collapse
          isOpen={isOpen}
          className="filter-body"
        >
          <div className="filter-form upload-date-filter-form">
            {dateRanges.map(r => (
              <FormGroup key={`${dateMode}-${r.id}`}>
                <Label for={`${prefix}${dateMode}-radioUploadDate-${r.id}`} check>
                  <Input
                    type="radio"
                    name={this.getFilterFieldName()}
                    id={`${prefix}${dateMode}-radioUploadDate-${r.id}`}
                    className="input-radio radio-s"
                    checked={filter === r.id}
                    value={r.id}
                    onChange={this.handleChangeFilter}
                  />
                  {' '}
                  <span>
                    {r.label}
                  </span>
                </Label>
              </FormGroup>
            ))}
            {filter === 'custom' && (
              <FormGroup>
                <DateRangePicker
                  startDate={startDate}
                  startDateId={`${this.getFilterField()}_from`}
                  endDate={endDate}
                  endDateId={`${this.getFilterField()}_to`}
                  numberOfMonths={1}
                  minimumNights={0}
                  withPortal={true}
                  onDatesChange={this.handleChangeDates}
                  hideKeyboardShortcutsPanel={true}
                  noBorder={true}
                  customArrowIcon={<i className="far fa-long-arrow-right" />}
                  isOutsideRange={() => false}
                  focusedInput={focusedInput}
                  onFocusChange={fi => this.setState({ focusedInput: fi })}
                  renderMonthElement={params => (renderMonthElement(params, dateMode))}
                />
              </FormGroup>
            )}
          </div>
        </Collapse>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => ({
  statuses: state.Files.statuses,
  filters: props.customFilters || state.Files.filters,
});

const mapDispatchToProps = (dispatch, props) => ({
  addFilter: props.customAddFilter || ((filter) => {
    dispatch(addSelectedFilter(filter));
  }),
  removeFilter: props.customRemoveFilter || ((mode) => {
    dispatch(deleteSelectedFilter(mode));
  }),
  clearSelectedFilters: props.customClearSelectedFilters || ((mode) => {
    dispatch(deleteSelectedFilter(mode, null));
  }),
});

FiltersFileDates.propTypes = {
  intl: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  addFilter: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
  clearSelectedFilters: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  dateMode: PropTypes.string,
  handleChangeFilters: PropTypes.func.isRequired,
  handleToggleFilter: PropTypes.func.isRequired,
  prefix: PropTypes.string,
};

FiltersFileDates.defaultProps = {
  dateMode: 'upload',
  prefix: '',
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(FiltersFileDates));
